import { useMemo, useRef } from "react";
import {
  CONTENT_HUB_SOURCES,
  useContentHubSearch,
  flattenContentHubPages,
  type ContentHubFilter,
  type ContentHubItem,
} from "@/features/content-hub";
import type { Headline } from "../models/Headline";
import { mapContentHubItemToHeadline } from "./useContentHubHeadlinesFeed";

const getPublishedMs = (headline: Headline): number =>
  typeof headline.published_at === "object"
    ? headline.published_at.unix * 1000
    : new Date(headline.published_at).getTime();

interface Options {
  /** Gate the query — when false nothing is fetched. */
  enabled?: boolean;
}

/**
 * Content-hub-backed filtered search for the headlines widget. Compiles the
 * generalized `ContentHubFilter` to a filter tree, pins the newsquawk-headlines
 * source, and adapts the results to the widget's `Headline` model.
 *
 * Mirrors the slots the widget already consumed from the legacy
 * `useSearchHeadlines`, now cursor-paginated.
 */
export function useContentHubHeadlinesSearch(
  filter: ContentHubFilter,
  { enabled = true }: Options = {}
) {
  const query = useContentHubSearch(filter, {
    enabled,
    pinnedSource: CONTENT_HUB_SOURCES.HEADLINES,
  });

  // Stable row identity across refetches — the item component mutates
  // show_details on the object, so remapping the same item must return it.
  const mappedCacheRef = useRef(new WeakMap<ContentHubItem, Headline>());
  const mapItem = (item: ContentHubItem): Headline => {
    let headline = mappedCacheRef.current.get(item);
    if (!headline) {
      headline = mapContentHubItemToHeadline(item);
      mappedCacheRef.current.set(item, headline);
    }
    return headline;
  };
  const mapItemRef = useRef(mapItem);
  mapItemRef.current = mapItem;

  const headlines = useMemo(() => {
    return flattenContentHubPages(query.data)
      .map((item) => mapItemRef.current(item))
      .sort((a, b) => getPublishedMs(b) - getPublishedMs(a));
  }, [query.data]);

  return {
    headlines,
    isLoading: query.isPending && enabled,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage,
    loadNextPage: query.fetchNextPage,
    loadingNextPage: query.isFetchingNextPage,
  };
}
